import React, { useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { INDIAN_STATES, sanitizeGstin } from "../lib/constants";
import { X } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = {
  name: "", gstin: "", contact_person: "", phone: "", email: "",
  billing_address: "", shipping_address: "", city: "", state: "", state_code: "", pincode: "",
};

/**
 * Quick-add modal for Customer or Vendor — opens inline on document form.
 * onCreated(party) is called after successful save.
 */
export default function QuickPartyModal({ kind = "customer", onClose, onCreated }) {
  const [form, setForm] = useState({ ...EMPTY, kind });
  const [saving, setSaving] = useState(false);

  const onStateChange = (code) => {
    const st = INDIAN_STATES.find(s => s.code === code);
    setForm(f => ({ ...f, state_code: code, state: st?.name || "" }));
  };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/parties", form);
      toast.success(`${kind === "customer" ? "Customer" : "Vendor"} created`);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const label = kind === "customer" ? "Customer" : "Vendor";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="quick-party-modal">
      <div className="bg-white rounded-lg border border-border max-w-xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="font-display text-lg font-semibold">Quick Add {label}</div>
          <button onClick={onClose} className="btn-ghost" data-testid="close-quick-party"><X size={16} /></button>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <div className="label-overline block mb-1.5">Name *</div>
            <input className="field" autoFocus data-testid="qp-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <div className="label-overline block mb-1.5">GSTIN (15 char, leave empty for URP)</div>
            <div className="flex gap-1.5">
              <input className="field font-mono uppercase flex-1" data-testid="qp-gstin" value={form.gstin} onChange={e => setForm({ ...form, gstin: sanitizeGstin(e.target.value) })} placeholder="15-char GSTIN" />
              <button type="button" onClick={async () => {
                const g = (form.gstin || "").trim().toUpperCase();
                if (g.length !== 15 || g === "URP") { toast.error("Enter a 15-char GSTIN first"); return; }
                try {
                  const { data } = await api.get(`/gstin/lookup/${g}`);
                  const st = INDIAN_STATES.find(s => s.code === (data.state_code || ""));
                  setForm(f => ({
                    ...f,
                    name: data.legal_name || data.trade_name || f.name,
                    billing_address: data.address_line || f.billing_address,
                    city: data.city || f.city,
                    state_code: data.state_code || f.state_code,
                    state: (st?.name) || data.state || f.state,
                    pincode: data.pincode || f.pincode,
                  }));
                  toast.success(`Auto-filled: ${data.legal_name || data.trade_name}`);
                } catch (err) {
                  toast.error(formatApiError(err?.response?.data?.detail) || "GSTIN lookup failed");
                }
              }} className="btn-secondary !px-2.5 text-xs whitespace-nowrap" data-testid="qp-gstin-lookup">Look up</button>
            </div>
          </div>
          <div>
            <div className="label-overline block mb-1.5">Phone</div>
            <input className="field" data-testid="qp-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <div className="label-overline block mb-1.5">State *</div>
            <select className="field" data-testid="qp-state" value={form.state_code} onChange={e => onStateChange(e.target.value)}>
              <option value="">Select state</option>
              {INDIAN_STATES.map(s => <option key={s.code} value={s.code}>{s.code} - {s.name}</option>)}
            </select>
          </div>
          <div>
            <div className="label-overline block mb-1.5">City</div>
            <input className="field" data-testid="qp-city" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <div className="label-overline block mb-1.5">Billing Address</div>
            <textarea rows={2} className="field" data-testid="qp-billing" value={form.billing_address} onChange={e => setForm({ ...form, billing_address: e.target.value })} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-surface-muted">
          <button onClick={onClose} className="btn-secondary" data-testid="cancel-quick-party">Cancel</button>
          <button onClick={save} disabled={saving || !form.name} className="btn-primary" data-testid="save-quick-party">{saving ? "Saving…" : `Save ${label}`}</button>
        </div>
      </div>
    </div>
  );
}
